import React, { useEffect } from "react";
import styled from "styled-components";
import { ChevronLeft } from "react-feather";
import { Link } from "react-router-dom";
import { StagingBadge } from "@cinesys/ui/components";
import { useCustomerContext } from "../../hooks/useCustomerContext";
import CinesysLogo, { CINESYS_URL } from "../../components/CinesysLogo";
import MetadataImportMark from "./MetadataImportMark";
import { MI_BASE, MI_WORDMARK } from "./config";

/**
 * Metadata Import's own fully self-contained header — independent of `ShellHeader`, which only
 * covers customer-apps' own baseline surfaces, not Spotter/AB Vault/Metadata Import (see
 * `docs/decisions.md`). Structurally the same as `AbHeader`/`SpotterHeader` (Lockup: mark +
 * wordmark + `StagingBadge`; org breadcrumb; "Powered by" Cinesys badge) but on `--mi*` tokens. On
 * a workflow-detail screen the breadcrumb carries the workflow name and a "Workflows" back link
 * appears below; on the list screen neither is shown.
 */
const MOBILE_MAX_WIDTH = "700px";

const Header = styled.header`
  background: var(--miSlate);
  color: #e8ecef;
  padding: 13px 22px 0;
  position: sticky;
  top: 0;
  z-index: 30;

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    position: static;
  }
`;

const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: nowrap;
`;

const Lockup = styled(Link)`
  display: flex;
  align-items: center;
  gap: 9px;
  flex-shrink: 0;
  text-decoration: none;
  color: var(--miAccent);
`;

const Brand = styled.div`
  font-weight: 700;
  font-size: 21px;
  letter-spacing: -0.02em;
  color: #fff;
`;

const Crumb = styled.div`
  font-size: 15px;
  color: var(--miHeaderMuted);
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  span {
    color: var(--miHeaderStrong);
    font-weight: 600;
  }
`;

const CrumbSkeleton = styled.span`
  display: inline-block;
  width: 90px;
  height: 0.85em;
  border-radius: 3px;
  background: var(--miHeaderBarTrack);
  vertical-align: -0.1em;
`;

const Spacer = styled.div`
  flex: 1;
`;

const PoweredBy = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;
  opacity: 0.85;
  transition: opacity 0.15s ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    display: none;
  }
`;

const PoweredByLabel = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--miHeaderMuted);
  white-space: nowrap;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  color: var(--miHeaderMuted);
  text-decoration: none;
  font-size: 13px;
  padding: 6px 8px 6px 4px;
  border-radius: 6px;

  &:hover {
    color: #e8ecef;
  }
`;

/** Sets the browser tab title (and restores it on unmount) for this interface — same reasoning as
 *  `SpotterHeader`'s `useSpotterTabIdentity` (a global favicon in `public/index.html` would
 *  brand-leak into the other interfaces this SPA also serves). */
const useTabIdentity = () => {
  useEffect(() => {
    const previous = document.title;
    document.title = `${MI_WORDMARK} — CineSys`;
    return () => {
      document.title = previous;
    };
  }, []);
};

interface Props {
  /** Workflow name shown after the org in the breadcrumb (workflow detail only). */
  crumb?: string;
}

const MetadataImportHeader = ({ crumb }: Props) => {
  const { data, isLoading } = useCustomerContext();
  const orgName = data?.customerName;
  useTabIdentity();

  return (
    <Header>
      <Top>
        <Lockup to={MI_BASE}>
          <MetadataImportMark />
          <Brand>{MI_WORDMARK}</Brand>
          <StagingBadge />
        </Lockup>
        <Crumb>
          {orgName ? (
            <span>{orgName}</span>
          ) : isLoading ? (
            <CrumbSkeleton aria-hidden="true" />
          ) : null}
          {crumb ? (
            <>
              {" "}
              &nbsp;/&nbsp; <span>{crumb}</span>
            </>
          ) : null}
        </Crumb>
        <Spacer />
        <PoweredBy href={CINESYS_URL} target="_blank" rel="noopener noreferrer">
          <PoweredByLabel>Powered by</PoweredByLabel>
          <CinesysLogo height={25} />
        </PoweredBy>
      </Top>

      {crumb && (
        <NavRow>
          <BackLink to={MI_BASE}>
            <ChevronLeft size={16} />
            Workflows
          </BackLink>
        </NavRow>
      )}
    </Header>
  );
};

export default MetadataImportHeader;
